import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import classnames from "classnames";
import { BLOCK_TYPES } from "../../cores/websitePage/constants/blockTypes";
import { WEBSITE_PAGE_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useWebsitePageCore } from "../../cores/websitePage";
import { WebsiteBlock } from "../../cores/websitePage/interfaces/WebsiteBlock";
import { AiGenerateModal } from "../component/website/AiGenerateModal";
import { BlockLibrary } from "../component/website/BlockLibrary";
import { BlockProperties } from "../component/website/BlockProperties";
import { BlockRenderer } from "../component/website/BlockRenderer";
import {
  cloneBlockWithNewIds,
  deleteBlockInTree,
  findBlockInTree,
  updateBlockInTree,
} from "../component/website/blockTree.utils";
import { HTML_PAGE_BLOCK_TYPE, hasHybridHtmlPage } from "../../cores/websitePage/constants/htmlPageBlock";
import { PageSettings } from "../component/website/PageSettings";
import { WebsiteWidgetPreview } from "../component/website/WebsiteWidgetPreview";
import { WebsiteIcon } from "../component/website/WebsiteIcon";
import { getPreviewPageUrl, resolvePublicPageUrl } from "../component/website/websitePageUrls";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "./EditWebsitePage.scss";
import "../component/website/website-builder.scss";
import "../component/website/websiteBlocksResponsive.scss";

export default function EditWebsitePage() {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const { company } = useMeCore();
  const { goToWebsitePages } = useRouterCore();
  const {
    page,
    pageLoadFailed,
    editorContent,
    selectedBlockId,
    viewMode,
    getPage,
    savePage,
    deletePage,
    publishPage,
    unpublishPage,
    generateAi,
    setEditorContent,
    setSelectedBlock,
    setViewMode,
  } = useWebsitePageCore();

  const { setCollapseSideBar } = useGlobalCore();
  const [activeTab, setActiveTab] = useState<"editor" | "settings">("editor");
  const [showAiModal, setShowAiModal] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCollapseSideBar(true);
    return () => setCollapseSideBar(false);
  }, [setCollapseSideBar]);

  useEffect(() => {
    if (company?.id && id) getPage(company.id, id);
  }, [company, id, getPage]);

  const blocks = useMemo(() => editorContent.blocks ?? [], [editorContent.blocks]);

  const selectedBlock = useMemo(
    () => (selectedBlockId ? findBlockInTree(blocks, selectedBlockId) : null),
    [blocks, selectedBlockId]
  );

  const updateBlocks = useCallback(
    (nextBlocks: WebsiteBlock[]) => {
      setEditorContent({ ...editorContent, blocks: nextBlocks });
    },
    [editorContent, setEditorContent]
  );

  const handleAddBlock = useCallback(
    (block: WebsiteBlock) => {
      updateBlocks([...blocks, block]);
      setSelectedBlock(block.id);
    },
    [blocks, setSelectedBlock, updateBlocks]
  );

  const handleUpdateBlock = useCallback(
    (updated: WebsiteBlock) => {
      updateBlocks(updateBlockInTree(blocks, updated));
    },
    [blocks, updateBlocks]
  );

  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      updateBlocks(deleteBlockInTree(blocks, blockId));
      if (selectedBlockId === blockId) setSelectedBlock(null);
    },
    [blocks, selectedBlockId, setSelectedBlock, updateBlocks]
  );

  const moveBlock = useCallback(
    (blockId: string, direction: -1 | 1) => {
      const index = blocks.findIndex((b) => b.id === blockId);
      if (index < 0) return;
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= blocks.length) return;
      const nextBlocks = [...blocks];
      const [removed] = nextBlocks.splice(index, 1);
      nextBlocks.splice(newIndex, 0, removed);
      updateBlocks(nextBlocks);
    },
    [blocks, updateBlocks]
  );

  const duplicateBlock = useCallback(
    (blockId: string) => {
      const index = blocks.findIndex((b) => b.id === blockId);
      if (index < 0) return;
      const clone = cloneBlockWithNewIds(blocks[index]);
      const nextBlocks = [...blocks];
      nextBlocks.splice(index + 1, 0, clone);
      updateBlocks(nextBlocks);
      setSelectedBlock(clone.id);
    },
    [blocks, setSelectedBlock, updateBlocks]
  );

  const handleDeletePage = useCallback(async () => {
    if (!company?.id || !id) return;
    if (!window.confirm(intl.formatMessage({ id: "app.websitePage.confirmDelete" }))) return;
    await deletePage(company.id, id);
    goToWebsitePages();
  }, [company, deletePage, goToWebsitePages, id, intl]);

  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const nextBlocks = [...blocks];
    const [removed] = nextBlocks.splice(draggedIndex, 1);
    nextBlocks.splice(index, 0, removed);
    setDraggedIndex(index);
    updateBlocks(nextBlocks);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  const handleSave = useCallback(async () => {
    if (!company?.id || !id) return;
    setIsSaving(true);
    try {
      await savePage(company.id, id, editorContent);
    } finally {
      setIsSaving(false);
    }
  }, [company, editorContent, id, savePage]);

  const handlePublish = useCallback(async () => {
    if (!company?.id || !id) return;
    setIsPublishing(true);
    try {
      await savePage(company.id, id, editorContent);
      await publishPage(company.id, id);
    } finally {
      setIsPublishing(false);
    }
  }, [company, editorContent, id, publishPage, savePage]);

  const handleUnpublish = useCallback(async () => {
    if (!company?.id || !id) return;
    await unpublishPage(company.id, id);
  }, [company, id, unpublishPage]);

  const handleAiGenerate = useCallback(
    async (data: { prompt: string; companyName: string; industry: string }) => {
      if (!company?.id) return;
      setIsGeneratingAi(true);
      try {
        const content = await generateAi(company.id, data);
        setEditorContent(content);
      } finally {
        setIsGeneratingAi(false);
      }
    },
    [company, generateAi, setEditorContent]
  );

  const getBlockLabel = useCallback(
    (type: string) => {
      const def = BLOCK_TYPES.find((b) => b.type === type);
      return def ? intl.formatMessage({ id: def.labelKey }) : type;
    },
    [intl]
  );

  useEffect(() => {
    if (!selectedBlockId) return;
    const blockEl = canvasScrollRef.current?.querySelector(
      `[data-editor-block-id="${selectedBlockId}"]`
    );
    blockEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedBlockId]);

  useEffect(() => {
    if (selectedBlockId) return;
    const navbar = blocks.find((b) => b.type === "navbar");
    if (navbar) setSelectedBlock(navbar.id);
  }, [blocks, selectedBlockId, setSelectedBlock]);

  const canvasWidth = viewMode === "desktop" ? "100%" : viewMode === "tablet" ? "768px" : "375px";
  const isHybridHtmlPage = hasHybridHtmlPage(blocks);
  const deviceLabels = useMemo(
    () => ({
      desktop: intl.formatMessage({ id: "app.websitePage.editor.deviceDesktop" }),
      tablet: intl.formatMessage({ id: "app.websitePage.editor.deviceTablet" }),
      mobile: intl.formatMessage({ id: "app.websitePage.editor.deviceMobile" }),
    }),
    [intl]
  );

  const previewWidgets = useMemo(
    () =>
      company?.id
        ? {
            companyId: company.id,
            includeWhatsapp: page?.includeWhatsapp ?? true,
            formId: page?.formId ?? null,
          }
        : undefined,
    [company?.id, page?.formId, page?.includeWhatsapp]
  );

  return (
    <Layout>
      <Content className="wp-editor-wrap">
        <UpdateLoadingCard loadingId={WEBSITE_PAGE_LOADING} />
        <div className="wp-builder wp-editor">
          {pageLoadFailed && !page ? (
            <div className="wp-editor__load-error" role="alert">
              <WebsiteIcon icon="exclamation-triangle" />
              <h2>{intl.formatMessage({ id: "app.websitePage.editor.loadErrorTitle" })}</h2>
              <p>{intl.formatMessage({ id: "app.websitePage.editor.loadError" })}</p>
              <div className="wp-editor__load-error-actions">
                <button type="button" className="wp-btn wp-btn--secondary" onClick={goToWebsitePages}>
                  {intl.formatMessage({ id: "app.websitePage.editor.back" })}
                </button>
                {company?.id && id && (
                  <button
                    type="button"
                    className="wp-btn wp-btn--primary"
                    onClick={() => getPage(company.id, id)}
                  >
                    {intl.formatMessage({ id: "app.websitePage.editor.retry" })}
                  </button>
                )}
              </div>
            </div>
          ) : (
          <>
          <header className="wp-editor__toolbar">
            <div className="wp-editor__toolbar-row">
              <div className="wp-editor__toolbar-left">
                <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm wp-editor__back-btn" onClick={goToWebsitePages}>
                  <WebsiteIcon icon="arrow-left" />
                  {intl.formatMessage({ id: "app.websitePage.editor.back" })}
                </button>
                <div className="wp-editor__page-info">
                  <h1 className="wp-editor__page-name">{page?.name || "…"}</h1>
                  {page && <span className="wp-editor__page-slug">/{page.slug}</span>}
                </div>
                {page && (
                  <span className={`wp-badge wp-badge--${page.status}`}>
                    {page.status === "published"
                      ? intl.formatMessage({ id: "app.websitePage.status.published" })
                      : intl.formatMessage({ id: "app.websitePage.status.draft" })}
                  </span>
                )}
              </div>

              <div className="wp-editor__toolbar-center">
                <div className="wp-editor__tabs">
                  <button
                    type="button"
                    className={classnames("wp-editor__tab", { "wp-editor__tab--active": activeTab === "editor" })}
                    onClick={() => setActiveTab("editor")}
                  >
                    <WebsiteIcon icon="pen-ruler" />
                    {intl.formatMessage({ id: "app.websitePage.tabs.editor" })}
                  </button>
                  <button
                    type="button"
                    className={classnames("wp-editor__tab", { "wp-editor__tab--active": activeTab === "settings" })}
                    onClick={() => setActiveTab("settings")}
                  >
                    <WebsiteIcon icon="cog" />
                    {intl.formatMessage({ id: "app.websitePage.tabs.settings" })}
                  </button>
                </div>
              </div>

              <div className="wp-editor__toolbar-right">
                {page && company && (
                  <button
                    type="button"
                    className="wp-btn wp-btn--ghost wp-btn--sm"
                    onClick={() => {
                      if (!company) return;
                      const url =
                        page.status === "published"
                          ? resolvePublicPageUrl(page.publicUrl)
                          : getPreviewPageUrl(company.id, page.id);
                      window.open(url, "_blank");
                    }}
                  >
                    <WebsiteIcon icon="eye" />
                    <span className="wp-editor__btn-label">{intl.formatMessage({ id: "app.websitePage.editor.preview" })}</span>
                  </button>
                )}
                <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm" onClick={handleSave} disabled={isSaving}>
                  <WebsiteIcon icon="save" />
                  <span className="wp-editor__btn-label">
                    {isSaving
                      ? intl.formatMessage({ id: "app.websitePage.saving" })
                      : intl.formatMessage({ id: "app.websitePage.save" })}
                  </span>
                </button>
                {page?.status === "published" ? (
                  <button type="button" className="wp-btn wp-btn--secondary wp-btn--sm" onClick={handleUnpublish}>
                    {intl.formatMessage({ id: "app.websitePage.unpublish" })}
                  </button>
                ) : (
                  <button type="button" className="wp-btn wp-btn--primary wp-btn--sm" onClick={handlePublish} disabled={isPublishing}>
                    <WebsiteIcon icon="rocket" />
                    <span className="wp-editor__btn-label">
                      {isPublishing
                        ? intl.formatMessage({ id: "app.websitePage.publishing" })
                        : intl.formatMessage({ id: "app.websitePage.publish" })}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className="wp-btn wp-btn--danger wp-btn--sm"
                  onClick={handleDeletePage}
                >
                  <WebsiteIcon icon="trash-alt" />
                  <span className="wp-editor__btn-label">{intl.formatMessage({ id: "app.websitePage.editor.deletePage" })}</span>
                </button>
              </div>
            </div>
          </header>

          {activeTab === "editor" && (
            <div className="wp-editor__mobile-panels">
              <button
                type="button"
                className={classnames("wp-editor__mobile-panel-btn", { "is-active": leftPanelOpen })}
                onClick={() => setLeftPanelOpen((open) => !open)}
                aria-pressed={leftPanelOpen}
              >
                <WebsiteIcon icon="th-large" />
                {intl.formatMessage({ id: "app.websitePage.editor.mobileBlocks" })}
              </button>
              <button
                type="button"
                className={classnames("wp-editor__mobile-panel-btn", { "is-active": rightPanelOpen })}
                onClick={() => setRightPanelOpen((open) => !open)}
                aria-pressed={rightPanelOpen}
              >
                <WebsiteIcon icon="sliders-h" />
                {intl.formatMessage({ id: "app.websitePage.editor.mobileProperties" })}
              </button>
            </div>
          )}

          {activeTab === "editor" ? (
            <div
              className={classnames("wp-editor__workspace", {
                "wp-editor__workspace--no-left": !leftPanelOpen,
                "wp-editor__workspace--no-right": !rightPanelOpen,
                "wp-editor__workspace--html-page": isHybridHtmlPage,
              })}
            >
              <aside className={classnames("wp-editor__panel wp-editor__panel--left", { "wp-editor__panel--closed": !leftPanelOpen })}>
                <div className="wp-editor__panel-header">
                  <WebsiteIcon icon="th-large" />
                  <span>{intl.formatMessage({ id: "app.websitePage.editor.addBlocks" })}</span>
                  <button type="button" className="wp-editor__panel-toggle" onClick={() => setLeftPanelOpen(false)}>
                    <WebsiteIcon icon="chevron-left" />
                  </button>
                </div>
                <div className="wp-editor__panel-body">
                  <BlockLibrary onAddBlock={handleAddBlock} />
                </div>
                <div className="wp-editor__panel-footer">
                  <button type="button" className="wp-editor__ai-btn" onClick={() => setShowAiModal(true)}>
                    <WebsiteIcon icon="wand-magic-sparkles" />
                    <span>{intl.formatMessage({ id: "app.websitePage.ai.button" })}</span>
                    <small>{intl.formatMessage({ id: "app.websitePage.ai.creditsShort" })}</small>
                  </button>
                </div>
              </aside>

              {!leftPanelOpen && (
                <button type="button" className="wp-editor__panel-reopen wp-editor__panel-reopen--left" onClick={() => setLeftPanelOpen(true)}>
                  <WebsiteIcon icon="th-large" />
                </button>
              )}

              <main className="wp-editor__canvas-area">
                {isHybridHtmlPage && (
                  <div className="wp-editor__hybrid-hint">
                    <WebsiteIcon icon="info-circle" />
                    {intl.formatMessage({ id: "app.websitePage.editor.hybridHint" })}
                  </div>
                )}
                <div className="wp-editor__browser">
                  <div className="wp-editor__browser-bar">
                    <div className="wp-editor__browser-dots">
                      <span /><span /><span />
                    </div>
                    <div className="wp-editor__browser-url">
                      <WebsiteIcon icon="lock" />
                      {page ? `${window.location.host}/p/…/${page.slug}` : "…"}
                    </div>
                    <div className="wp-editor__devices-btns">
                      {(["desktop", "tablet", "mobile"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          className={classnames("wp-editor__device-btn", { "wp-editor__device-btn--active": viewMode === mode })}
                          onClick={() => setViewMode(mode)}
                          title={deviceLabels[mode]}
                          aria-label={deviceLabels[mode]}
                          aria-pressed={viewMode === mode}
                        >
                          <WebsiteIcon icon={mode === "desktop" ? "desktop" : mode === "tablet" ? "tablet-alt" : "mobile-alt"} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="wp-editor__canvas-scroll" ref={canvasScrollRef}>
                    <div
                      className={classnames("wp-editor__canvas", {
                        "wp-editor__canvas--tablet": viewMode === "tablet",
                        "wp-editor__canvas--mobile": viewMode === "mobile",
                      })}
                      style={{ width: canvasWidth }}
                    >
                      {blocks.length === 0 ? (
                        <div className="wp-editor__canvas-empty">
                          <div className="wp-editor__canvas-empty-icon">
                            <WebsiteIcon icon="mouse-pointer" />
                          </div>
                          <h4>{intl.formatMessage({ id: "app.websitePage.editor.emptyTitle" })}</h4>
                          <p>{intl.formatMessage({ id: "app.websitePage.editor.empty" })}</p>
                        </div>
                      ) : (
                        blocks.map((block, index) => (
                          <div
                            key={block.id}
                            data-editor-block-id={block.id}
                            onDragOver={(e) => handleDragOver(e, index)}
                            className={classnames("wp-editor__block", {
                              "wp-editor__block--selected": selectedBlockId === block.id,
                              "wp-editor__block--dragging": draggedIndex === index,
                              "wp-editor__block--navbar": block.type === "navbar",
                              "wp-editor__block--html-page": block.type === HTML_PAGE_BLOCK_TYPE,
                            })}
                          >
                            <div
                              className="wp-editor__block-handle"
                              draggable
                              onDragStart={() => handleDragStart(index)}
                              onDragEnd={handleDragEnd}
                              title={intl.formatMessage({ id: "app.websitePage.editor.dragHint" })}
                            >
                              <WebsiteIcon icon="grip-vertical" />
                            </div>
                            <div className="wp-editor__block-toolbar">
                              <span className="wp-editor__block-toolbar-type">{getBlockLabel(block.type)}</span>
                              <button
                                type="button"
                                title={intl.formatMessage({ id: "app.websitePage.props.moveUp" })}
                                disabled={index === 0}
                                onClick={(e) => { e.stopPropagation(); moveBlock(block.id, -1); }}
                              >
                                <WebsiteIcon icon="arrow-up" />
                              </button>
                              <button
                                type="button"
                                title={intl.formatMessage({ id: "app.websitePage.props.moveDown" })}
                                disabled={index === blocks.length - 1}
                                onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 1); }}
                              >
                                <WebsiteIcon icon="arrow-down" />
                              </button>
                              <button
                                type="button"
                                title={intl.formatMessage({ id: "app.websitePage.editor.duplicate" })}
                                onClick={(e) => { e.stopPropagation(); duplicateBlock(block.id); }}
                              >
                                <WebsiteIcon icon="clone" />
                              </button>
                              <button
                                type="button"
                                className="wp-editor__block-toolbar-delete"
                                title={intl.formatMessage({ id: "app.websitePage.props.delete" })}
                                onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.id); }}
                              >
                                <WebsiteIcon icon="trash-alt" />
                              </button>
                            </div>
                            <div className="wp-editor__block-content">
                              <BlockRenderer
                                block={block}
                                isEditing
                                selectedBlockId={selectedBlockId}
                                onSelect={setSelectedBlock}
                                onUpdate={handleUpdateBlock}
                                widgets={previewWidgets}
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <WebsiteWidgetPreview
                      companyId={company?.id}
                      includeWhatsapp={page?.includeWhatsapp}
                    />
                  </div>
                </div>
              </main>

              {!rightPanelOpen && (
                <button type="button" className="wp-editor__panel-reopen wp-editor__panel-reopen--right" onClick={() => setRightPanelOpen(true)}>
                  <WebsiteIcon icon="sliders-h" />
                </button>
              )}

              <aside className={classnames("wp-editor__panel wp-editor__panel--right", { "wp-editor__panel--closed": !rightPanelOpen })}>
                <div className="wp-editor__panel-body wp-editor__panel-body--properties">
                  <BlockProperties
                    block={selectedBlock}
                    onUpdate={handleUpdateBlock}
                    onDelete={handleDeleteBlock}
                    onMoveUp={(blockId) => moveBlock(blockId, -1)}
                    onMoveDown={(blockId) => moveBlock(blockId, 1)}
                  />
                </div>
                <button type="button" className="wp-editor__panel-toggle wp-editor__panel-toggle--inline" onClick={() => setRightPanelOpen(false)}>
                  <WebsiteIcon icon="chevron-right" />
                </button>
              </aside>
            </div>
          ) : (
            <div className="wp-editor__settings-area">
              {page && company && <PageSettings page={page} companyId={company.id} />}
            </div>
          )}

          <AiGenerateModal
            isOpen={showAiModal}
            onClose={() => setShowAiModal(false)}
            onGenerate={handleAiGenerate}
            isLoading={isGeneratingAi}
          />
          </>
          )}
        </div>
      </Content>
    </Layout>
  );
}
